import React, { useState, useContext, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Container, 
  Paper,
  Divider,
  Link,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import authService from '../services/authService';
import passwordValidationService from '../services/passwordValidationService';
import PasswordValidation from '../components/PasswordValidation';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Start in sign-up mode when arriving via /app/register or ?mode=signup
  // (e.g. the landing-page "Get Started Free" CTA), otherwise default to sign in.
  const [showSignIn, setShowSignIn] = useState(() => {
    const path = window.location.pathname.toLowerCase();
    const params = new URLSearchParams(window.location.search);
    return !(path.includes('/register') || params.get('mode') === 'signup');
  });
  const [name, setName] = useState('');
  const [passwordConfig, setPasswordConfig] = useState(null);
  const [passwordValidation, setPasswordValidation] = useState({ valid: true, errors: [], requirements: {} });
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
  const { login, register, googleLogin, appleLogin, error, isLoading, currentUser, handleAuthCallback, setError, logout, fetchUserData } = useContext(AuthContext);
  const navigate = useAppNavigation();
  const rawNavigate = useNavigate(); // For direct path navigation
  const location = useLocation();
  const oauthProcessedRef = useRef(false); // Track if OAuth has been processed
  const errorRef = useRef(null); // Error alert, scrolled into view when shown

  // Bring the error message into view when it appears. On small screens the
  // submit button sits below the fold, so an error rendered at the top of the
  // form would otherwise be invisible to the user.
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Load password configuration on component mount
  useEffect(() => {
    const loadPasswordConfig = async () => {
      const config = await passwordValidationService.getPasswordConfig();
      setPasswordConfig(config);
    };
    loadPasswordConfig();
  }, []);

  // Validate password in real-time when in registration mode
  useEffect(() => {
    if (!showSignIn && passwordConfig && passwordConfig.enabled) {
      const validation = passwordValidationService.validatePassword(
        password, 
        passwordConfig.confirmation_required ? confirmPassword : null
      );
      setPasswordValidation(validation);
    }
  }, [password, confirmPassword, showSignIn, passwordConfig]);

  // Reset form state when switching between sign in and sign up
  useEffect(() => {
    setPassword('');
    setConfirmPassword('');
    setShowPasswordRequirements(false);
    setPasswordValidation({ valid: true, errors: [], requirements: {} });
    setAgreeToTerms(false);
    setAgreeToPrivacy(false);
  }, [showSignIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (showSignIn) {
      const success = await login(email, password);
      if (success) {
        // Check for return path
        const returnPath = sessionStorage.getItem('returnPath');
        if (returnPath) {
          sessionStorage.removeItem('returnPath');
          rawNavigate(returnPath);
        } else {
          navigate.to.HOME();
        }
      }
    } else {
      // Registration requires name, email, and password
      if (!name) {
        setError('Name is required for registration');
        return;
      }

      // Validate password if validation is enabled. Surface the specific
      // problem(s) — e.g. "Passwords do not match" — rather than a vague
      // "fix the requirements" message.
      if (passwordConfig && passwordConfig.enabled && !passwordValidation.valid) {
        const specifics = (passwordValidation.errors || []).join('. ');
        setError(specifics || 'Please fix the password requirements before continuing.');
        return;
      }

      // Validate legal consent checkboxes
      if (!agreeToTerms) {
        setError('You must agree to the Terms of Service to create an account');
        return;
      }
      
      if (!agreeToPrivacy) {
        setError('You must acknowledge the Privacy Policy to create an account');
        return;
      }
      
      // Prepare registration data
      const registrationData = { 
        full_name: name, 
        email, 
        password 
      };

      // Add password confirmation if required
      if (passwordConfig && passwordConfig.confirmation_required) {
        registrationData.confirm_password = confirmPassword;
      }
      
      const success = await register(registrationData);
      if (success) {
        // Check for return path
        const returnPath = sessionStorage.getItem('returnPath');
        if (returnPath) {
          sessionStorage.removeItem('returnPath');
          rawNavigate(returnPath);
        } else {
          navigate.to.HOME();
        }
      }
    }
  };

  // Handle Google login
  const handleGoogleLogin = () => {
    googleLogin();
  };

  // Handle Apple login
  const handleAppleLogin = () => {
    appleLogin();
  };

  // Handle OAuth callback
  useEffect(() => {
    console.log('Login useEffect running, location.search:', location.search);
    const query = new URLSearchParams(location.search);
    const token = query.get('token');
    const code = query.get('code');
    console.log('Parsed token:', token, 'code:', code);
    
    // Only process if we have a token or code and haven't processed yet
    if ((token || code) && !oauthProcessedRef.current) {
      oauthProcessedRef.current = true; // Mark as processed immediately to prevent double processing
      
      if (token) {
        console.log('OAuth token received, processing...', token);
        
        // Clear any existing errors
        setError(null);
        
        // Set the token and fetch user data
        authService.setToken(token);
        
        fetchUserData().then(() => {
          console.log('User data fetched successfully, navigating...');
          
          // Clean up URL by removing the token parameter
          const cleanUrl = new URL(window.location);
          cleanUrl.searchParams.delete('token');
          window.history.replaceState({}, document.title, cleanUrl.toString());
          
          // Check for return path
          const returnPath = sessionStorage.getItem('returnPath');
          if (returnPath) {
            console.log('Redirecting to stored path:', returnPath);
            sessionStorage.removeItem('returnPath');
            rawNavigate(returnPath);
          } else {
            console.log('No return path, navigating to home');
            navigate.to.HOME();
          }
        }).catch((error) => {
          console.error('Error fetching user data after OAuth:', error);
          setError('Authentication failed. Please try again.');
          oauthProcessedRef.current = false; // Reset on error to allow retry
          
          // Clean up the token on error
          authService.removeToken();
          
          // Clean up URL
          const cleanUrl = new URL(window.location);
          cleanUrl.searchParams.delete('token');
          window.history.replaceState({}, document.title, cleanUrl.toString());
        });
      } else if (code) {
        console.log('OAuth code received, processing...', code);
        
        // Handle auth code (legacy flow)
        handleAuthCallback(code).catch((error) => {
          console.error('Error handling auth callback:', error);
          setError('Authentication failed. Please try again.');
          oauthProcessedRef.current = false; // Reset on error to allow retry
          
          // Clean up URL
          const cleanUrl = new URL(window.location);
          cleanUrl.searchParams.delete('code');
          window.history.replaceState({}, document.title, cleanUrl.toString());
        });
      }
    }
  }, [location.search, navigate, handleAuthCallback, fetchUserData, setError, rawNavigate]);

  // Handle logout parameter for testing
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('logout') === 'true') {
      logout();
    }
  }, [location.search, logout]);

  // Redirect if already authenticated (but not during OAuth processing)
  useEffect(() => {
    if (currentUser && !oauthProcessedRef.current) {
      // Only redirect if we're not in the middle of OAuth processing
      const query = new URLSearchParams(location.search);
      const hasOAuthParams = query.get('token') || query.get('code');
      
      if (!hasOAuthParams) {
        // Check if there's a stored return path (e.g., from family invitation)
        const returnPath = sessionStorage.getItem('returnPath');
        if (returnPath) {
          sessionStorage.removeItem('returnPath');
          rawNavigate(returnPath);
        } else {
          navigate.to.HOME();
        }
      }
    }
  }, [currentUser, navigate, rawNavigate, location.search]);

  // Toggle between sign in and sign up
  const toggleSignIn = () => {
    setShowSignIn(!showSignIn);
    // Clear error when switching modes
    if (error) setError(null);
  };

  return (
    <Container component="main" maxWidth="xs" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
      <Paper 
        elevation={3}
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          borderRadius: 2
        }}
      >
        <Typography 
          component="h1" 
          variant="h4" 
          align="center"
          sx={{ mb: 3, fontWeight: 600 }}
        >
          ClutterLogix
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          {!showSignIn && (
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              autoFocus={!showSignIn}
              sx={{ mb: 2 }}
            />
          )}
          
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            autoFocus={showSignIn}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert ref={errorRef} severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete={showSignIn ? "current-password" : "new-password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (!showSignIn && passwordConfig && passwordConfig.enabled) {
                setShowPasswordRequirements(true);
              }
            }}
            disabled={isLoading}
            sx={{ mb: !showSignIn && passwordConfig && passwordConfig.enabled ? 1 : 3 }}
          />

          {/* Password validation component for registration */}
          {!showSignIn && passwordConfig && (
            <PasswordValidation
              requirements={passwordValidation.requirements}
              requirementsText={passwordValidationService.getPasswordRequirementsText()}
              show={showPasswordRequirements && passwordConfig.enabled}
              errors={passwordValidation.errors}
            />
          )}

          {/* Password confirmation field for registration */}
          {!showSignIn && passwordConfig && passwordConfig.confirmation_required && (
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              error={confirmPassword && password !== confirmPassword}
              helperText={confirmPassword && password !== confirmPassword ? "Passwords do not match" : ""}
              sx={{ mb: 3 }}
            />
          )}
          
          {/* Legal consent checkboxes for registration */}
          {!showSignIn && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    I agree to the{' '}
                    <Link
                      href="/app/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Terms of Service
                    </Link>
                  </Typography>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToPrivacy}
                    onChange={(e) => setAgreeToPrivacy(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    I have read and understand the{' '}
                    <Link
                      href="/app/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </Box>
          )}
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isLoading || (!showSignIn && (!agreeToTerms || !agreeToPrivacy))}
            sx={{ 
              py: 1.5,
              mb: 2,
              fontWeight: 600,
              fontSize: '1rem'
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              showSignIn ? 'Sign In with Email' : 'Sign Up with Email'
            )}
          </Button>
          
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>
          
          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            sx={{ 
              py: 1.5, 
              mb: 2,
              fontSize: '1rem'
            }}
          >
            Continue with Google
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            onClick={handleAppleLogin}
            disabled={isLoading}
            sx={{ 
              py: 1.5, 
              mb: 2,
              fontSize: '1rem',
              backgroundColor: '#000',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#333',
                color: '#fff'
              }
            }}
          >
            Continue with Apple
          </Button>
          
          {showSignIn ? (
            <>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Link href="#" variant="body2" onClick={(e) => { e.preventDefault(); navigate.to.FORGOT_PASSWORD(); }}>
                  Forgot password?
                </Link>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  New to ClutterLogix?
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={toggleSignIn}
                  disabled={isLoading}
                  sx={{ py: 1.25, fontWeight: 600, fontSize: '1rem' }}
                >
                  Create a free account
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link href="#" variant="body2" onClick={(e) => { e.preventDefault(); toggleSignIn(); }}>
                Already have an account? Sign in
              </Link>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
